import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { api, getUtm } from "@/lib/sleek";
import { fireConversion } from "@/lib/gtag";

const TIMES = ["10:30 AM", "1:00 PM", "2:30 PM", "4:00 PM"];
const OTHER_TIME = "Other";
const PROJECTS = ["Kitchen Remodel", "Bath Remodel", "Whole-Home Renovation", "Showroom Visit"];

// Map portfolio category → booking project_type option
const CATEGORY_TO_PROJECT = {
  Kitchen: "Kitchen Remodel",
  Bath: "Bath Remodel",
  "Whole-Home": "Whole-Home Renovation",
};

export default function Booking() {
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState();
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "Kitchen Remodel", preferred_time: "10:30 AM", address: "", notes: "", ref: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [inspiration, setInspiration] = useState(null);
  const [otherTime, setOtherTime] = useState(false);

  // Pre-fill from ?ref=p22&title=...&cat=Kitchen (Portfolio lightbox "Get this look")
  useEffect(() => {
    const ref = searchParams.get("ref");
    const title = searchParams.get("title");
    const cat = searchParams.get("cat");
    if (!ref && !title) return;
    setInspiration({ ref, title, cat });
    const isProject = ref && /^p\d+$/.test(ref);
    let notes = "";
    if (title) {
      if (isProject) notes = `Inspired by your "${title}" project. I'd love to talk about doing something similar.`;
      else if (/tour|visit/i.test(title)) notes = "I'd like to schedule a showroom tour.";
      else if (/estimate/i.test(title)) notes = `I'd like to request a ${title.toLowerCase()}.`;
      else if (/consultation/i.test(title)) notes = `I'd like to schedule a ${title.toLowerCase()}.`;
      else notes = `I'd like to talk about ${title}.`;
    }
    setForm((f) => ({
      ...f,
      project_type: CATEGORY_TO_PROJECT[cat] || f.project_type,
      ref: ref || "",
      notes: notes || f.notes,
    }));
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Please pick a date."); return; }
    if (!form.preferred_time.trim()) { toast.error(otherTime ? "Please suggest a time that works for you." : "Please pick a time."); return; }
    setSubmitting(true);
    try {
      const localDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      await api.post("/bookings", { ...form, ...getUtm(), preferred_date: localDate });
      fireConversion("booking");
      setDone(true);
      toast.success("Consultation booked — we'll confirm by email shortly.");
    } catch (e) {
      toast.error("Booking failed. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-[800px] px-6 py-32 text-center">
        <div className="font-display text-4xl md:text-5xl font-light">You&apos;re on the calendar.</div>
        <p className="text-zinc-600 mt-6 leading-relaxed">We&apos;ll email you a confirmation and a calendar invite within one business day. In the meantime, feel free to text us at (781) 760-4524.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
      <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Free Consultation</div>
      <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight mt-4 max-w-5xl leading-[1.0]">Book a one-hour design conversation.</h1>
      <p className="text-zinc-600 max-w-2xl mt-8 leading-relaxed text-lg">No pressure, no obligation. We&apos;ll talk through your space, your priorities, and rough budget — and follow up with a clear next step.</p>

      {inspiration && inspiration.title && (
        <div
          className="mt-10 inline-flex items-start gap-3 border-l-2 border-zinc-950 pl-4 py-2 max-w-2xl"
          data-testid="booking-inspiration-banner"
        >
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Inspiration loaded</div>
            <div className="font-display text-xl mt-1">{inspiration.title}</div>
            <div className="text-sm text-zinc-600 mt-1">
              Your message below is pre-filled — feel free to edit it.
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-12 mt-16" data-testid="booking-form">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 mb-4">Choose a date</div>
          <div className="border border-zinc-200 p-3 bg-white inline-block">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().toDateString()) || d.getDay() === 0}
              data-testid="booking-calendar"
            />
          </div>
          <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 mt-8 mb-3">Choose a time</div>
          <div className="flex flex-wrap gap-2">
            {TIMES.map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => { setOtherTime(false); setForm({ ...form, preferred_time: t }); }}
                className={`px-4 py-2 border text-sm ${!otherTime && form.preferred_time === t ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`}
                data-testid={`booking-time-${t.replace(/\s|:/g, "")}`}
              >{t}</button>
            ))}
            <button
              type="button"
              onClick={() => { setOtherTime(true); setForm({ ...form, preferred_time: "" }); }}
              className={`px-4 py-2 border text-sm ${otherTime ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`}
              data-testid="booking-time-other"
            >Suggest a time</button>
          </div>
          {otherTime && (
            <div className="mt-4 max-w-xs">
              <input
                type="text"
                placeholder="e.g. 5:30 PM"
                value={form.preferred_time}
                onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                className="w-full border-b border-zinc-300 bg-transparent py-2 text-sm focus:outline-none focus:border-zinc-950"
                data-testid="booking-time-custom-input"
              />
              <p className="text-xs text-zinc-500 mt-2">Suggest a time that works for you — we&apos;ll confirm availability before finalizing.</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="booking-name" />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="booking-email" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="booking-phone" />
          <div>
            <label className="text-xs tracking-[0.25em] uppercase text-zinc-500">Project type</label>
            <select
              value={form.project_type}
              onChange={(e) => setForm({ ...form, project_type: e.target.value })}
              className="w-full border-b border-zinc-300 bg-transparent py-2 focus:outline-none focus:border-zinc-950"
              data-testid="booking-project"
            >
              {PROJECTS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <Field label="Home address (optional)" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="booking-address" className="md:col-span-2" />
          <div className="md:col-span-2">
            <label className="text-xs tracking-[0.25em] uppercase text-zinc-500">Anything else?</label>
            <textarea
              rows={4}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full border-b border-zinc-300 bg-transparent py-2 focus:outline-none focus:border-zinc-950 resize-none"
              data-testid="booking-notes"
            />
          </div>
          <button disabled={submitting} className="md:col-span-2 bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition disabled:opacity-50" data-testid="booking-submit">
            {submitting ? "Booking…" : "Confirm consultation"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs tracking-[0.25em] uppercase text-zinc-500">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border-b border-zinc-300 bg-transparent py-2 focus:outline-none focus:border-zinc-950"
        data-testid={testid}
      />
    </div>
  );
}
